// Table.js (Updated)
import React, { useState, useEffect, useRef } from "react";
import Draggable from "react-draggable";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import "./Table.css";
import useUserStore from "../../stores/store";
import { getTableStatusColor, placeChairs, shouldBlinkATable } from "../../services/order-table-base-service";
import { TABLE_RESIZE_RATIO } from "../../constant/constants";
import { Chair } from "./TableMiniComponents";

function DinningTable({ data, mode, onDragStop, onResizeStop, onTableClick, isSelected }) {
  const nodeRef = useRef(null);
  const { isSuperAdmin } = useUserStore();
  const { id, x, y, width, height, baseHeight, baseWidth, status, name, capacity, shape, occupiedCount } = data;

  const tableCursorStyle = {
    cursor: isSuperAdmin ? (mode === "view" ? "auto" : "grab") : "pointer",
  };

  // 2. Set up state to hold the constraints
  // We initialize it with the desired proportions
  const [currentConstraints, setConstraints] = useState({
    min: [baseWidth * TABLE_RESIZE_RATIO.MIN, baseHeight * TABLE_RESIZE_RATIO.MIN],
    max: [baseWidth * TABLE_RESIZE_RATIO.MAX, baseHeight * TABLE_RESIZE_RATIO.MAX],
  });

  // 3. A ref to track if we are currently resizing
  const isResizing = useRef(false);

  const tableStyle = {
    width: `${width}px`,
    height: `${height}px`,
    backgroundColor: getTableStatusColor(status),
    borderRadius: shape === "Circle" ? "50%" : shape === "Oval" ? "50px" : "8px",
  };

  // 3. Dynamic Chair Logic
  // This is the tricky part. We can't use hard-coded positions anymore.
  let chairs = [];
  const chairWidth = 15; // <-- ADD THIS (matches w-[15px])
  const chairHeight = 20; // <-- ADD THIS (matches h-[20px])
  const chairOffset = 15; // How far (px) to place the chair from the table edge

  if (shape === "Circle") {
    // This logic places chairs in a circle/ellipse
    const numChairs = capacity;
    const radiusX = width / 2;
    const radiusY = height / 2;

    for (let i = 0; i < numChairs; i++) {
      const angle = (i / numChairs) * 2 * Math.PI; // Angle in radians

      // 1. Calculate rotation in degrees
      // We subtract 90 degrees because a 0-degree angle is on the "right" (x-axis),
      // but a 0-degree CSS rotation (your un-rotated chair) faces "up".
      // This adjustment makes the "up" of the chair point "inward".
      const rotation = angle * (180 / Math.PI) - 90 + 180;

      // Calculate position
      const left = width / 2 + (radiusX + chairOffset) * Math.cos(angle) - chairWidth / 2;
      const top = height / 2 + (radiusY + chairOffset) * Math.sin(angle) - chairHeight / 2;

      chairs.push({ top: `${top}px`, left: `${left}px`, transform: `rotate(${rotation}deg)` });
    }
  } else {
    // --- NEW DYNAMIC LOGIC FOR RECTANGLES/SQUARES ---
    let numTop, numBottom, numLeft, numRight;

    // --- SPECIAL CASE FOR CAPACITY 2 ---
    // This will force 2 chairs to be placed on the left and right (X-axis).
    if (capacity === 2) {
      numTop = 0;
      numBottom = 0;
      numLeft = 1;
      numRight = 1;
    } else {
      // 1. Determine how many chairs go on horizontal vs vertical sides
      const horizontalRatio = width / (width + height);
      let numHorizontal = Math.round(capacity * horizontalRatio);
      // Ensure horizontal is an even number for balance
      if (numHorizontal % 2 !== 0 && capacity > 1) {
        numHorizontal = numHorizontal + 1 > capacity ? numHorizontal - 1 : numHorizontal + 1;
      }
      let numVertical = capacity - numHorizontal;
      // 2. Split them between top/bottom and left/right
      numTop = Math.ceil(numHorizontal / 2);
      numBottom = Math.floor(numHorizontal / 2);
      numLeft = Math.ceil(numVertical / 2);
      numRight = Math.floor(numVertical / 2);
    }

    // 4. Place all the chairs
    chairs = chairs.concat(placeChairs(numTop, "top", chairWidth, chairHeight, chairOffset));
    chairs = chairs.concat(placeChairs(numBottom, "bottom", chairWidth, chairHeight, chairOffset));
    chairs = chairs.concat(placeChairs(numLeft, "left", chairWidth, chairHeight, chairOffset));
    chairs = chairs.concat(placeChairs(numRight, "right", chairWidth, chairHeight, chairOffset));
  }

  // 5. This handler runs when the resize *starts*
  const handleResizeStart = (e, { size }) => {
    isResizing.current = true; // Set flag
    // Set the constraints *once* based on the size at the start
    setConstraints({
      min: [baseWidth * TABLE_RESIZE_RATIO.MIN, baseHeight * TABLE_RESIZE_RATIO.MIN],
      max: [baseWidth * TABLE_RESIZE_RATIO.MAX, baseHeight * TABLE_RESIZE_RATIO.MAX],
    });
  };

  // 6. This handler runs when the resize *stops*
  const handleResizeStop = (e, { size }) => {
    isResizing.current = false; // Clear flag
  };

  const handleTableClick = () => {
    if (mode === "view" && onTableClick) {
      onTableClick(data.id);
    }
  };

  // 4. This effect updates the constraints *only when not resizing*
  // This syncs the state *after* a resize is complete.
  useEffect(() => {
    if (!isResizing.current) {
      setConstraints({
        min: [baseWidth * TABLE_RESIZE_RATIO.MIN, baseHeight * TABLE_RESIZE_RATIO.MIN],
        max: [baseWidth * TABLE_RESIZE_RATIO.MAX, baseHeight * TABLE_RESIZE_RATIO.MAX],
      });
    }
  }, [width, height]); // Re-runs only when the final width/height props change

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".drag-handle" // Only allow dragging from a specific element
      position={{ x: x, y: y }} // Controlled position
      onStop={(e, data) => onDragStop(id, e, data)}
      bounds="parent" // Keeps it inside the canvas
      disabled={mode !== "edit"}
    >
      <Resizable
        width={width}
        height={height}
        onResize={(e, { size }) => onResizeStop(id, e, null, size)}
        // 7. Add these handlers to manage the local constraints
        onResizeStart={handleResizeStart}
        onResizeStop={handleResizeStop}
        // 8. Use the constraints from our state
        minConstraints={currentConstraints.min}
        maxConstraints={currentConstraints.max}
        lockAspectRatio={true}
        resizeHandles={mode === "edit" ? ["se"] : []}
      >
        <div ref={nodeRef} className={`table`} style={tableStyle} onClick={handleTableClick}>
          {/* Chairs */}
          {chairs.map((style, index) => (
            <Chair key={index} style={style} isOccupied={index < occupiedCount} />
          ))}

          {/* Table Content */}
          <div className="table-content drag-handle" style={tableCursorStyle}>
            {" "}
            {/* This is the drag handle */}
            <div className="flex flex-row items-center">
              <img className="w-[20px] h-[12px]" src="/images/icons/daining-table.svg" />
              <strong className="text-[14px] ml-[6px] font-bold">{name}</strong>
            </div>
            <div className="flex flex-row items-center">
              <img className="w-[11px] h-[11px]" src="/images/icons/user-man-svgrepo-com.svg"/>
              <strong className="text-[11px] ml-[5px]">{capacity > 9 ? capacity : "0"+capacity}</strong>
            </div>
          </div>
        </div>
      </Resizable>
    </Draggable>
  );
}

export default DinningTable;
